import { useMemo, useRef, useState } from "react";
import { orderForDisplay } from "./order";
import { useReasons } from "./useReasons";
import { needsText, textError } from "./validation";
import { FrictionCapture, MomentType, Reason, ReasonCapture } from "./types";

export type ReasonCaptureState = {
  moment: MomentType;
  multiple: boolean;
  loading: boolean;
  error: boolean;
  retry: () => void;
  /** Motivos na ordem exibida (sorteada uma vez por montagem da janela). */
  ordered: Reason[];
  reasonPositions: number[];
  value: number[];
  toggle: (id: number) => void;
  text: string;
  setText: (text: string) => void;
  chosen: Reason[];
  textRequired: boolean;
  textError: string | null;
  valid: boolean;
  /** Corpo de motivo único (cancel, refusal, sla). Só chame com `valid`. */
  toCapture: () => ReasonCapture;
  /** Corpo do atrito com `has: true` (vários motivos). */
  toFriction: () => FrictionCapture;
};

const clean = (text: string) => {
  const trimmed = text.trim();
  return trimmed ? { text: trimmed } : {};
};

/** Estado de uma janela de motivo: lista, ordem embaralhada (item 29), escolha, texto e validade. */
export function useReasonCapture(moment: MomentType, { multiple = false, enabled = true } = {}): ReasonCaptureState {
  const query = useReasons(moment, enabled);
  const [value, setValue] = useState<number[]>([]);
  const [text, setText] = useState("");
  // Sorteio uma vez por lista recebida: digitar ou marcar não reordena; recarregar a lista (Tentar de novo) sim.
  const random = useRef(Math.random);
  const { ordered, reasonPositions } = useMemo(() => orderForDisplay(query.data ?? [], random.current), [query.data]);
  const chosen = ordered.filter((r) => value.includes(r.id));
  const textRequired = needsText(chosen);
  const error = textError(textRequired, text);
  const toggle = (id: number) =>
    setValue((current) => (multiple ? (current.includes(id) ? current.filter((v) => v !== id) : [...current, id]) : [id]));
  return {
    moment,
    multiple,
    loading: query.isLoading,
    error: query.isError,
    retry: () => void query.refetch(),
    ordered,
    reasonPositions,
    value,
    toggle,
    text,
    setText,
    chosen,
    textRequired,
    textError: error,
    valid: chosen.length > 0 && !error,
    toCapture: () => ({ reasonId: chosen[0]?.id, ...clean(text), reasonPositions }),
    toFriction: () => ({ has: true, reasonIds: chosen.map((r) => r.id), ...clean(text), reasonPositions }),
  };
}
